import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button, Tabs } from '@/components/ui';
import { EmptyState } from '@/components/feedback/EmptyState';
import { ErrorState } from '@/components/feedback/ErrorState';
import { SkeletonList } from '@/components/feedback/Skeleton';
import { PageHeader } from '@/components/layout/PageHeader';
import { routePaths } from '@/app/routes';
import { useActivePatient } from '@/features/patients/hooks/useActivePatient';
import { useResolver } from '@/features/nomenclature/hooks/useResolver';
import { useAppointments } from '../hooks/useAppointments';
import { AppointmentCard } from '../components/AppointmentCard';

type TabValue = 'upcoming' | 'past';

export function AppointmentsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { patient } = useActivePatient();
  const [tab, setTab] = useState<TabValue>('upcoming');

  const { upcoming, past, isLoading, isError, refetch } = useAppointments(patient?.fhirId ?? null);
  const { data: resolver = null } = useResolver();
  const list = tab === 'upcoming' ? upcoming : past;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t('appointments.title')}
        subtitle={t('appointments.subtitle')}
        actions={
          <Button size="sm" onClick={() => navigate(routePaths.newAppointment)}>
            {t('booking.new_cta')}
          </Button>
        }
      />

      <Tabs<TabValue>
        value={tab}
        onChange={setTab}
        items={[
          { value: 'upcoming', label: t('appointments.upcoming'), count: upcoming.length },
          { value: 'past', label: t('appointments.past'), count: past.length },
        ]}
      />

      {isLoading ? (
        <SkeletonList rows={3} />
      ) : isError ? (
        <ErrorState description={t('appointments.load_error')} onRetry={() => void refetch()} />
      ) : list.length === 0 ? (
        <EmptyState
          title={
            tab === 'upcoming' ? t('appointments.empty_upcoming') : t('appointments.empty_past')
          }
          description={t('appointments.empty_hint')}
        />
      ) : (
        <div className="flex flex-col gap-3">
          {list.map((appointment) => (
            <AppointmentCard
              key={`${appointment.startTime.toISOString()}-${appointment.physicianId}`}
              appointment={appointment}
              variant={tab}
              resolver={resolver}
            />
          ))}
        </div>
      )}
    </div>
  );
}
